import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { createOrder } from "@/lib/api";
import { KNOW_IT_OWLS_LOGO } from "@/data/products";
import { optimizedImg } from "@/lib/img";
import DiscountCodeInput from "@/components/DiscountCodeInput";

/* Checkout — custom multi-section form that collects everything BEFORE we
 * hand the customer off to Stripe. Stripe will then only need a payment
 * method (card details). All canonical order data lives in our Mongo
 * `orders` collection and is referenced via `order_id` metadata.
 */

const blankAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "USA",
};

const Field = ({ label, name, value, onChange, required, type = "text", placeholder, testId, autoComplete }) => (
  <label className="block">
    <span className="font-heading uppercase tracking-[0.2em] text-[10px] md:text-xs text-[var(--st-fg)]/70">
      {label}
      {required && <span className="text-[var(--st-maroon)]"> *</span>}
    </span>
    <input
      data-testid={testId}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className="mt-1 w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2.5 font-body text-base text-[var(--st-fg)] outline-none focus:bg-white focus:ring-0 focus:border-[var(--st-maroon)] transition-colors"
    />
  </label>
);

const Section = ({ title, num, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    className="border-2 border-[#111] bg-[var(--st-bg)] shadow-brutal-sm"
  >
    <header className="flex items-center gap-3 border-b-2 border-[#111] bg-[var(--st-mustard)] px-4 py-2.5">
      <span className="font-pixel text-2xl text-[var(--st-fg)]">{String(num).padStart(2, "0")}</span>
      <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl text-[var(--st-fg)]">
        {title}
      </h2>
    </header>
    <div className="p-4 md:p-5">{children}</div>
  </motion.section>
);

const AddressGrid = ({ value, onChange, prefix, testIdPrefix }) => {
  const upd = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-12">
        <Field
          label="Street address"
          name={`${prefix}_line1`}
          value={value.line1}
          onChange={upd("line1")}
          required
          placeholder="100 Owl Avenue"
          testId={`${testIdPrefix}-line1`}
          autoComplete={`${prefix}-address-line1`}
        />
      </div>
      <div className="col-span-12">
        <Field
          label="Apt / Suite (optional)"
          name={`${prefix}_line2`}
          value={value.line2 || ""}
          onChange={upd("line2")}
          testId={`${testIdPrefix}-line2`}
          autoComplete={`${prefix}-address-line2`}
        />
      </div>
      <div className="col-span-12 md:col-span-6">
        <Field
          label="City"
          name={`${prefix}_city`}
          value={value.city}
          onChange={upd("city")}
          required
          testId={`${testIdPrefix}-city`}
          autoComplete={`${prefix}-address-level2`}
        />
      </div>
      <div className="col-span-6 md:col-span-3">
        <Field
          label="State"
          name={`${prefix}_state`}
          value={value.state}
          onChange={upd("state")}
          required
          testId={`${testIdPrefix}-state`}
          autoComplete={`${prefix}-address-level1`}
        />
      </div>
      <div className="col-span-6 md:col-span-3">
        <Field
          label="ZIP / Postal"
          name={`${prefix}_zip`}
          value={value.postal_code}
          onChange={upd("postal_code")}
          required
          testId={`${testIdPrefix}-zip`}
          autoComplete={`${prefix}-postal-code`}
        />
      </div>
      <div className="col-span-12 md:col-span-6">
        <Field
          label="Country"
          name={`${prefix}_country`}
          value={value.country}
          onChange={upd("country")}
          required
          testId={`${testIdPrefix}-country`}
          autoComplete={`${prefix}-country-name`}
        />
      </div>
    </div>
  );
};

export default function Checkout() {
  const navigate = useNavigate();
  const { items, inc, dec, remove, discount, discountCode } = useCart();

  const [customer, setCustomer] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [shipping, setShipping] = useState({ ...blankAddress });
  const [billing, setBilling] = useState({ ...blankAddress });
  const [billingSame, setBillingSame] = useState(true);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const updCustomer = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  // Local totals for display only — backend recomputes from Mongo prices.
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const shippingFeeBase = subtotal >= 100 || subtotal === 0 ? 0 : 6.5;
  const discountAmount = discount?.discount_amount || 0;
  const subtotalAfter = discount?.subtotal_after ?? subtotal;
  const shippingFee = discount?.free_shipping ? 0 : shippingFeeBase;
  const tax = +(subtotalAfter * 0.0875).toFixed(2);
  const grand = +(subtotalAfter + shippingFee + tax).toFixed(2);

  const validate = () => {
    if (!items.length) return "Your bag is empty.";
    const c = customer;
    if (!c.email || !/^\S+@\S+\.\S+$/.test(c.email)) return "Enter a valid email.";
    if (!c.first_name || !c.last_name) return "Enter your full name.";
    const need = (a, label) => {
      for (const k of ["line1", "city", "state", "postal_code", "country"]) {
        if (!a[k]) return `${label}: ${k.replace("_", " ")} is required.`;
      }
      return null;
    };
    const sErr = need(shipping, "Shipping");
    if (sErr) return sErr;
    if (!billingSame) {
      const bErr = need(billing, "Billing");
      if (bErr) return bErr;
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const { url } = await createOrder({
        items,
        customer,
        shipping_address: shipping,
        billing_address: billingSame ? null : billing,
        notes: notes || null,
        discount_code: discount?.code || null,
      });
      if (!url) throw new Error("No checkout URL returned");
      // Hand off to Stripe.
      window.location.href = url;
    } catch (ex) {
      console.error(ex);
      toast.error(ex?.response?.data?.detail || "Could not start checkout. Try again.");
      setSubmitting(false);
    }
  };

  if (!items.length) {
    return (
      <div data-testid="checkout-empty" className="min-h-screen grid-paper flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal p-8 text-center">
          <img src={KNOW_IT_OWLS_LOGO} alt="The Know It Owls" className="h-16 mx-auto mb-4" />
          <p className="font-display uppercase text-3xl text-[var(--st-fg)]/40">Empty bag</p>
          <p className="font-body text-sm text-[var(--st-fg)]/60 mt-2 mb-6">
            Class hasn&apos;t started yet. Pick something off the syllabus.
          </p>
          <Link to="/" data-testid="checkout-back-home" className="btn-brutal inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-[var(--st-bg)]">
      {/* slim brand header */}
      <header className="border-b-2 border-[#111] bg-[var(--st-bg)]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="checkout-logo-link">
            <img src={KNOW_IT_OWLS_LOGO} alt="The Know It Owls" className="h-10" />
            <div className="leading-tight">
              <p className="font-display uppercase tracking-tight text-base text-[var(--st-maroon)]">
                Substitute
              </p>
              <p className="font-display uppercase tracking-tight text-base text-[var(--st-mustard)] -mt-1">
                Teacher
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-2 font-heading uppercase tracking-[0.22em] text-[10px] md:text-xs text-[var(--st-fg)]/60">
            <Lock size={12} /> Secure Checkout
          </div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-[1200px] mx-auto px-4 md:px-6 py-8 grid grid-cols-12 gap-6">
        {/* LEFT: form sections */}
        <div className="col-span-12 lg:col-span-7 xl:col-span-8 flex flex-col gap-5">
          <Section title="Contact" num={1}>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12">
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={customer.email}
                  onChange={updCustomer("email")}
                  required
                  placeholder="you@example.com"
                  testId="checkout-email"
                  autoComplete="email"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Field
                  label="First name"
                  name="first_name"
                  value={customer.first_name}
                  onChange={updCustomer("first_name")}
                  required
                  testId="checkout-first-name"
                  autoComplete="given-name"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Field
                  label="Last name"
                  name="last_name"
                  value={customer.last_name}
                  onChange={updCustomer("last_name")}
                  required
                  testId="checkout-last-name"
                  autoComplete="family-name"
                />
              </div>
              <div className="col-span-12">
                <Field
                  label="Phone (optional)"
                  name="phone"
                  type="tel"
                  value={customer.phone}
                  onChange={updCustomer("phone")}
                  testId="checkout-phone"
                  autoComplete="tel"
                />
              </div>
            </div>
          </Section>

          <Section title="Shipping Address" num={2}>
            <AddressGrid
              value={shipping}
              onChange={setShipping}
              prefix="shipping"
              testIdPrefix="checkout-shipping"
            />
          </Section>

          <Section title="Billing Address" num={3}>
            <label className="flex items-center gap-2 cursor-pointer select-none mb-4">
              <input
                type="checkbox"
                checked={billingSame}
                onChange={(e) => setBillingSame(e.target.checked)}
                data-testid="checkout-billing-same"
                className="w-4 h-4 accent-[var(--st-maroon)]"
              />
              <span className="font-body text-sm text-[var(--st-fg)]">
                My billing address is the same as my shipping address
              </span>
            </label>
            {!billingSame && (
              <AddressGrid
                value={billing}
                onChange={setBilling}
                prefix="billing"
                testIdPrefix="checkout-billing"
              />
            )}
          </Section>

          <Section title="Order Notes" num={4}>
            <label className="block">
              <span className="font-heading uppercase tracking-[0.2em] text-[10px] md:text-xs text-[var(--st-fg)]/70">
                Anything we should know? (optional)
              </span>
              <textarea
                data-testid="checkout-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Gate code, delivery preferences, gift message…"
                className="mt-1 w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2.5 font-body text-base outline-none focus:bg-white focus:border-[var(--st-maroon)] transition-colors"
              />
            </label>
          </Section>
        </div>

        {/* RIGHT: order summary */}
        <aside className="col-span-12 lg:col-span-5 xl:col-span-4">
          <div className="lg:sticky lg:top-6 border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal flex flex-col">
            <header className="border-b-2 border-[#111] bg-[var(--st-maroon)] text-white px-4 py-2.5">
              <h2 className="font-display uppercase tracking-tight text-xl">Your Bag · {items.length}</h2>
            </header>
            <ul className="divide-y-2 divide-[#11111122] px-4">
              {items.map((i) => (
                <li
                  key={i.key}
                  data-testid={`checkout-item-${i.key}`}
                  className="py-3 flex gap-3 items-start"
                >
                  <div className="w-16 h-20 border-2 border-[#111] overflow-hidden shrink-0">
                    <img
                      src={optimizedImg(i.image, 160)}
                      alt={i.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display uppercase tracking-tight text-sm leading-tight">
                      {i.name}
                    </p>
                    <p className="font-heading uppercase tracking-[0.18em] text-[10px] text-[var(--st-fg)]/60 mt-1">
                      <span className="font-price text-base text-[var(--st-fg)]/80 tracking-tight align-middle">
                        ${i.price.toFixed(2)}
                      </span>{" "}
                      each
                      {i.size && (
                        <>
                          {" · "}
                          <span className="text-[var(--st-maroon)]">Size {i.size}</span>
                        </>
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => dec(i.key)}
                        data-testid={`checkout-dec-${i.key}`}
                        className="w-7 h-7 border-2 border-[#111] hover:bg-[var(--st-mustard)] flex items-center justify-center text-sm"
                      >
                        −
                      </button>
                      <span className="font-display text-sm">{i.quantity}</span>
                      <button
                        type="button"
                        onClick={() => inc(i.key)}
                        data-testid={`checkout-inc-${i.key}`}
                        className="w-7 h-7 border-2 border-[#111] hover:bg-[var(--st-mustard)] flex items-center justify-center text-sm"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(i.key)}
                        data-testid={`checkout-remove-${i.key}`}
                        className="ml-auto text-[var(--st-maroon)] text-xs hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="font-price text-lg text-[var(--st-fg)]">${(i.price * i.quantity).toFixed(2)}</p>
                </li>
              ))}
            </ul>

            <DiscountCodeInput variant="checkout" />

            <div className="px-4 py-3 border-t-2 border-[#111] space-y-1">
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} testId="checkout-subtotal" />
              {discount && (
                <Row
                  label={`Discount · ${discount.code}`}
                  value={`−$${discountAmount.toFixed(2)}`}
                  testId="checkout-discount"
                  emphasis
                />
              )}
              <Row
                label="Shipping"
                value={shippingFee === 0 ? "Free" : `$${shippingFee.toFixed(2)}`}
                testId="checkout-shipping-fee"
              />
              <Row label="Tax (est.)" value={`$${tax.toFixed(2)}`} testId="checkout-tax" />
              <div className="border-t-2 border-[#111] mt-2 pt-2 flex items-center justify-between">
                <span className="font-display uppercase text-base">Total</span>
                <span data-testid="checkout-total" className="font-price text-3xl text-[var(--st-maroon)]">
                  ${grand.toFixed(2)}
                </span>
              </div>
              {!discount && subtotal < 100 && subtotal > 0 && (
                <p className="font-body text-[11px] text-[var(--st-fg)]/60 mt-1">
                  Spend ${(100 - subtotal).toFixed(2)} more for free shipping.
                </p>
              )}
              {discount && grand === 0 && (
                <p
                  data-testid="checkout-giveaway-note"
                  className="font-body text-[11px] text-[var(--st-maroon)] mt-1"
                >
                  Media giveaway · this order will complete with no payment required.
                </p>
              )}
            </div>

            <div className="px-4 pb-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid="checkout-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-5 py-3 font-heading uppercase tracking-[0.18em] text-sm hover:bg-[var(--st-mustard)] hover:text-[var(--st-fg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-brutal-sm"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Redirecting…
                  </>
                ) : grand === 0 && discount ? (
                  <>
                    Place Free Order <ArrowRight size={14} />
                  </>
                ) : (
                  <>
                    Continue to Payment <ArrowRight size={14} />
                  </>
                )}
              </button>
              <div className="mt-3 flex items-center gap-2 justify-center font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60">
                <ShieldCheck size={12} /> 256-bit Stripe encryption
              </div>
              <Link
                to="/"
                data-testid="checkout-keep-shopping"
                className="block mt-3 text-center font-heading uppercase tracking-[0.22em] text-[11px] text-[var(--st-maroon)] hover:underline"
              >
                ← Keep shopping
              </Link>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Row = ({ label, value, testId, emphasis }) => (
  <div className="flex items-center justify-between text-sm">
    <span
      className={`font-heading uppercase tracking-[0.18em] text-xs ${
        emphasis ? "text-[var(--st-maroon)]" : "text-[var(--st-fg)]/70"
      }`}
    >
      {label}
    </span>
    <span
      data-testid={testId}
      className={`font-price text-lg ${
        emphasis ? "text-[var(--st-maroon)]" : "text-[var(--st-fg)]"
      }`}
    >
      {value}
    </span>
  </div>
);
